/**
 * `PlanEditView` — single plan editor.
 *
 * Two modes (selected by `planId`):
 *   - `planId === null`     → create. The form is empty; Save POSTs
 *                             a new plan then PUTs prices + entitlements.
 *   - `planId === '<id>'`   → edit. The form hydrates from
 *                             GET /internal/admin/plans/:id; Save
 *                             PATCHes the plan, then PUTs prices +
 *                             entitlements.
 *
 * The save sequence is intentionally three calls in order — the API
 * exposes them as separate routes so the staff can correct one piece
 * (e.g. fix a price) without re-saving the whole catalog row.
 *
 * INERT (Phase A): saving here does NOT enforce limits or charge
 * anyone. The banner at the top of the form makes that unmistakable;
 * the "behavior" select on each entitlement is labeled "(applies once
 * enforcement ships)" so a staff member doesn't think setting
 * `hard` blocks traffic today.
 *
 * Archive: a PATCH with `{status:'archived'}`. Soft action — existing
 * subscriptions on the plan stay, but the route refuses NEW
 * assignments to it (HTTP 409 to the assignSubscription call).
 */

import { ArrowLeft, Archive } from 'lucide-react';
import { useEffect, useState } from 'react';

import type {
  AdminPlanCatalogVersion,
  AdminPlanDetail,
  AdminPlanDetailResponse,
  AdminPlanDimension,
  AdminPlanEntitlementBehavior,
  AdminPlanFeature,
  AdminPlanScope,
  AdminPlanStatus,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Field } from '../../components/atoms/Field.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { InertBanner } from './InertBanner.js';
import {
  ALL_DIMENSIONS,
  ALL_V2_DIMENSIONS,
  BEHAVIOR_OPTIONS,
  DIMENSION_SCOPE_MIRROR,
  SOFT_ONLY_DIMENSIONS_MIRROR,
  dimensionHint,
  dimensionLabel,
  dimensionsForScope,
  featureHint,
  featureLabel,
  featuresForScope,
  findPrice,
  planStatusTone,
  tenXMonthly,
} from './plan-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const PLAN_STATUS_OPTIONS: readonly AdminPlanStatus[] = [
  'draft',
  'active',
  'archived',
];

interface EntitlementRow {
  /** Empty string = unlimited on save. */
  readonly limit: string;
  readonly behavior: AdminPlanEntitlementBehavior;
  /** True if the staff member has explicitly added an entitlement for
   *  this dimension OR the loaded plan had one. False = the row will be
   *  omitted from the PUT (no quota defined). */
  readonly enabled: boolean;
}

interface FormState {
  name: string;
  slug: string;
  description: string;
  status: AdminPlanStatus;
  is_custom: boolean;
  trial_enabled: boolean;
  trial_days: string; // input UX
  // ADR-0263 §2.1 — scope + catalog_version + is_default +
  // included_site_plan_id. scope is CREATE-only (immutable).
  scope: AdminPlanScope;
  catalog_version: AdminPlanCatalogVersion;
  is_default: boolean;
  included_site_plan_id: string | null;
  // ADR-0263 §7.1 — overage allowance as a percent on top of each
  // finite metered limit. Stored as a string for the input UX; sent
  // as a number on the wire. "" → 0 on save.
  overage_pct: string;
  monthly_amount: string;
  monthly_currency: string;
  yearly_amount: string;
  yearly_currency: string;
  entitlements: Record<AdminPlanDimension, EntitlementRow>;
  // ADR-0263 §2.4 — feature set. Checkboxes gated by plan scope.
  features: ReadonlySet<AdminPlanFeature>;
}

function emptyEntitlements(): Record<AdminPlanDimension, EntitlementRow> {
  const out = {} as Record<AdminPlanDimension, EntitlementRow>;
  for (const d of ALL_DIMENSIONS) {
    out[d] = { limit: '', behavior: 'hard', enabled: false };
  }
  for (const d of ALL_V2_DIMENSIONS) {
    // soft-only dimensions get 'soft' as the fixed starting behavior.
    const beh: AdminPlanEntitlementBehavior = SOFT_ONLY_DIMENSIONS_MIRROR.has(d)
      ? 'soft'
      : 'hard';
    out[d] = { limit: '', behavior: beh, enabled: false };
  }
  return out;
}

function detailToForm(detail: AdminPlanDetailResponse): FormState {
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  const entitlements = emptyEntitlements();
  for (const e of detail.entitlements) {
    entitlements[e.dimension] = {
      limit: e.limit_value === null ? '' : String(e.limit_value),
      behavior: SOFT_ONLY_DIMENSIONS_MIRROR.has(e.dimension) ? 'soft' : e.behavior,
      enabled: true,
    };
  }
  return {
    name: detail.plan.name,
    slug: detail.plan.slug,
    description: detail.plan.description ?? '',
    status: detail.plan.status,
    is_custom: detail.plan.is_custom,
    trial_enabled: detail.plan.trial_enabled,
    trial_days: String(detail.plan.trial_days),
    scope: detail.plan.scope,
    catalog_version: detail.plan.catalog_version,
    is_default: detail.plan.is_default,
    included_site_plan_id: detail.plan.included_site_plan_id,
    overage_pct: String(detail.plan.overage_pct ?? 0),
    monthly_amount: monthly === undefined ? '' : String(monthly.amount_minor),
    monthly_currency: monthly?.currency ?? 'usd',
    yearly_amount: yearly === undefined ? '' : String(yearly.amount_minor),
    yearly_currency: yearly?.currency ?? 'usd',
    entitlements,
    features: new Set(detail.features),
  };
}

function blankForm(): FormState {
  return {
    name: '',
    slug: '',
    description: '',
    status: 'draft',
    is_custom: false,
    trial_enabled: false,
    trial_days: '14',
    // ADR-0263 §2.1 — new plans default to v2 + per-site scope.
    scope: 'site',
    catalog_version: 2,
    is_default: false,
    included_site_plan_id: null,
    // ADR-0263 §7.1 — new plans default to zero overage.
    overage_pct: '0',
    monthly_amount: '',
    monthly_currency: 'usd',
    yearly_amount: '',
    yearly_currency: 'usd',
    entitlements: emptyEntitlements(),
    features: new Set(),
  };
}

export interface PlanEditViewProps {
  setView: SetView;
  /** null = create mode; string = edit mode. */
  planId: string | null;
}

export function PlanEditView({ setView, planId }: PlanEditViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const isCreate = planId === null;

  const [detail, setDetail] = useState<AdminPlanDetailResponse | null>(null);
  const [form, setForm] = useState<FormState>(() => blankForm());
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successAt, setSuccessAt] = useState<number | null>(null);

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // ADR-0263 §2.1 — account plans may point at an "included" site
  // plan (what agency plan clients get). Load the catalog once so the
  // select has non-archived site plans to pick from.
  const [sitePlans, setSitePlans] = useState<readonly AdminPlanDetail[]>([]);

  useEffect(() => {
    if (isCreate) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getPlan(planId);
        if (cancelled) return;
        setDetail(res);
        setForm(detailToForm(res));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        if (err instanceof ApiError && err.kind === 'not_found') {
          setLoadError('Plan not found.');
          return;
        }
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setLoadError(`Could not load plan${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isCreate, planId]);

  // Site-plan catalog fetch — only needed when editing/creating an
  // account-scope plan (included_site_plan_id). Best-effort; a failure
  // just leaves the select empty.
  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const res = await api.getPlans();
        if (!cancelled) setSitePlans(res.plans);
      } catch {
        /* ignore; the select stays empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((cur) => ({ ...cur, [key]: value }));
  };

  const updateEntitlement = (
    d: AdminPlanDimension,
    patch: Partial<EntitlementRow>,
  ): void => {
    setForm((cur) => ({
      ...cur,
      entitlements: {
        ...cur.entitlements,
        [d]: { ...cur.entitlements[d], ...patch },
      },
    }));
  };

  const validate = (): string | null => {
    if (form.name.trim().length === 0) return 'Plan name is required.';
    if (form.slug.trim().length === 0) return 'Slug is required.';
    if (form.trial_enabled) {
      const days = Number(form.trial_days);
      if (!Number.isFinite(days) || days < 0 || !Number.isInteger(days)) {
        return 'Trial days must be a non-negative integer.';
      }
    }
    // ADR-0263 §7.1 — overage_pct is an integer 0–100.
    const overage_trimmed = form.overage_pct.trim();
    if (overage_trimmed.length > 0) {
      const o = Number(overage_trimmed);
      if (!Number.isFinite(o) || o < 0 || o > 100 || !Number.isInteger(o)) {
        return 'Overage allowance must be a whole number between 0 and 100.';
      }
    }
    for (const [interval, amount] of [
      ['monthly', form.monthly_amount],
      ['yearly', form.yearly_amount],
    ] as const) {
      const trimmed = amount.trim();
      if (trimmed.length === 0) continue;
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        return `${interval} amount must be a non-negative whole number in minor units.`;
      }
    }
    for (const d of ALL_DIMENSIONS) {
      const row = form.entitlements[d];
      if (!row.enabled) continue;
      const trimmed = row.limit.trim();
      if (trimmed.length === 0) continue; // blank = unlimited
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        return `${dimensionLabel(d)} limit must be a non-negative whole number, or blank for unlimited.`;
      }
    }
    return null;
  };

  const doSave = async (): Promise<void> => {
    if (saving) return;
    const v = validate();
    if (v !== null) {
      setSaveError(v);
      return;
    }
    setSaving(true);
    setSaveError(null);

    try {
      let effectivePlanId: string;

      if (isCreate) {
        const created = await api.createPlan({
          name: form.name.trim(),
          slug: form.slug.trim(),
          description:
            form.description.trim().length === 0 ? null : form.description.trim(),
          is_custom: form.is_custom,
          trial_enabled: form.trial_enabled,
          trial_days: Number(form.trial_days || '0'),
          // ADR-0263 §2.1 — scope + catalog_version are CREATE-only.
          scope: form.scope,
          catalog_version: form.catalog_version,
          included_site_plan_id:
            form.scope === 'account' ? form.included_site_plan_id : null,
          // ADR-0263 §7.1 — overage allowance (0–100).
          overage_pct: Number(form.overage_pct.trim() || '0'),
        });
        effectivePlanId = created.plan.plan_id;
        // Follow-up PATCH for post-create fields the POST does not accept:
        //   - status (POST seeds 'draft')
        //   - is_default (POST does not accept it; only active plans
        //     can be default — the server enforces that too)
        const createPatch: {
          status?: AdminPlanStatus;
          is_default?: boolean;
        } = {};
        if (form.status !== 'draft') createPatch.status = form.status;
        if (form.is_default && form.status === 'active') {
          createPatch.is_default = true;
        }
        if (Object.keys(createPatch).length > 0) {
          await api.updatePlan(effectivePlanId, createPatch);
        }
      } else {
        effectivePlanId = planId;
        // Only send changed fields on PATCH so we don't trigger the
        // server's `no_fields_to_update` 400 when staff Save without
        // touching anything (the prices + entitlements PUTs still run).
        const cur = detail?.plan;
        const draft: {
          name?: string;
          description?: string | null;
          status?: AdminPlanStatus;
          is_custom?: boolean;
          trial_enabled?: boolean;
          trial_days?: number;
          catalog_version?: AdminPlanCatalogVersion;
          is_default?: boolean;
          included_site_plan_id?: string | null;
          overage_pct?: number;
        } = {};
        if (cur !== undefined) {
          if (form.name.trim() !== cur.name) draft.name = form.name.trim();
          const nextDesc =
            form.description.trim().length === 0 ? null : form.description.trim();
          if (nextDesc !== (cur.description ?? null)) draft.description = nextDesc;
          if (form.status !== cur.status) draft.status = form.status;
          if (form.is_custom !== cur.is_custom) draft.is_custom = form.is_custom;
          if (form.trial_enabled !== cur.trial_enabled) {
            draft.trial_enabled = form.trial_enabled;
          }
          const td = Number(form.trial_days || '0');
          if (td !== cur.trial_days) draft.trial_days = td;
          if (form.catalog_version !== cur.catalog_version) {
            draft.catalog_version = form.catalog_version;
          }
          if (form.is_default !== cur.is_default) draft.is_default = form.is_default;
          // Only account plans carry an included_site_plan_id; for
          // site plans we never send it (even to null) since the
          // server validates against the plan's current scope.
          if (form.scope === 'account') {
            const nextIncl = form.included_site_plan_id;
            if (nextIncl !== cur.included_site_plan_id) {
              draft.included_site_plan_id = nextIncl;
            }
          }
          // ADR-0263 §7.1 — overage allowance.
          const nextOverage = Number(form.overage_pct.trim() || '0');
          if (nextOverage !== cur.overage_pct) {
            draft.overage_pct = nextOverage;
          }
        }
        if (Object.keys(draft).length > 0) {
          await api.updatePlan(effectivePlanId, draft);
        }
      }

      // Prices — assemble the full set (PUT replaces).
      const prices: Array<{
        interval: 'monthly' | 'yearly';
        amount_minor: number;
        currency: string;
      }> = [];
      if (form.monthly_amount.trim().length > 0) {
        prices.push({
          interval: 'monthly',
          amount_minor: Number(form.monthly_amount.trim()),
          currency: form.monthly_currency.trim().toLowerCase(),
        });
      }
      if (form.yearly_amount.trim().length > 0) {
        prices.push({
          interval: 'yearly',
          amount_minor: Number(form.yearly_amount.trim()),
          currency: form.yearly_currency.trim().toLowerCase(),
        });
      }
      await api.putPlanPrices(effectivePlanId, { prices });

      // Entitlements — only enabled rows go to the wire. The dimension
      // set follows the plan's catalog version + scope: v1 keeps the
      // original five, v2 renders only the dimensions scoped to the
      // plan (plus `retention_days` which is both-scope).
      const entitlementDims: readonly AdminPlanDimension[] =
        form.catalog_version === 2 ? dimensionsForScope(form.scope) : ALL_DIMENSIONS;
      const entitlements: Array<{
        dimension: AdminPlanDimension;
        limit_value: number | null;
        behavior: AdminPlanEntitlementBehavior;
      }> = [];
      for (const d of entitlementDims) {
        const row = form.entitlements[d];
        if (!row.enabled) continue;
        const trimmed = row.limit.trim();
        entitlements.push({
          dimension: d,
          limit_value: trimmed.length === 0 ? null : Number(trimmed),
          // Soft-only dimensions must send 'soft' (the server rejects
          // 'hard' with HTTP 400).
          behavior: SOFT_ONLY_DIMENSIONS_MIRROR.has(d) ? 'soft' : row.behavior,
        });
      }
      await api.putPlanEntitlements(effectivePlanId, { entitlements });

      // ADR-0263 §2.4 — features. Only valid for v2 plans; v1 plans
      // never carry features (the server would reject anyway on
      // scope mismatch for every feature). PUT replaces.
      if (form.catalog_version === 2) {
        const features = Array.from(form.features).filter((f) =>
          featuresForScope(form.scope).includes(f),
        );
        await api.putPlanFeatures(effectivePlanId, { features });
      }

      // Reload to canonical state.
      const fresh = await api.getPlan(effectivePlanId);
      setDetail(fresh);
      setForm(detailToForm(fresh));
      setSuccessAt(Date.now());

      if (isCreate) {
        // Navigate into the just-created plan's edit view so the URL
        // reflects the persisted plan_id.
        setView('plan-detail', { planId: effectivePlanId });
      }
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not save plan${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async (): Promise<void> => {
    if (archiving || planId === null) return;
    setArchiving(true);
    setSaveError(null);
    try {
      const res = await api.updatePlan(planId, { status: 'archived' });
      setDetail((cur) =>
        cur === null ? cur : { ...cur, plan: { ...cur.plan, ...res.plan } },
      );
      setForm((cur) => ({ ...cur, status: 'archived' }));
      setSuccessAt(Date.now());
      setArchiveOpen(false);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not archive plan${reason}.`);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <button
        type="button"
        onClick={() => setView('plans')}
        className="flex items-center gap-1 mb-4"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          background: 'transparent',
          border: 0,
          padding: 0,
          minHeight: 40,
        }}
      >
        <ArrowLeft size={12} /> Plan catalog
      </button>

      {!isCreate && loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      ) : loadError !== null ? (
        <Card style={{ padding: 16 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Load failed
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
            {loadError}
          </div>
        </Card>
      ) : (
        <>
          <PlanHeader
            isCreate={isCreate}
            name={form.name}
            status={form.status}
            scope={form.scope}
            isDefault={form.is_default}
            catalogVersion={form.catalog_version}
            planId={detail?.plan.plan_id ?? null}
            updatedAt={detail?.plan.updated_at ?? null}
          />

          <InertBanner flavor={form.catalog_version === 2 ? 'v2' : 'v1'} />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void doSave();
            }}
            className="grid grid-cols-1 gap-4"
          >
            <Card style={{ padding: 20 }}>
              <SectionKicker>Identity</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    className="w-full px-3 py-2 outline-none"
                    style={inputStyle(C)}
                    placeholder="Discovery"
                  />
                </Field>
                <Field
                  label="Slug"
                  hint="URL-safe identifier (lowercase, dashes)."
                  required
                >
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => update('slug', e.target.value)}
                    disabled={!isCreate}
                    className="w-full px-3 py-2 outline-none"
                    style={{
                      ...inputStyle(C),
                      fontFamily: monoFont,
                      opacity: isCreate ? 1 : 0.6,
                    }}
                    placeholder="discovery"
                  />
                </Field>
                <Field
                  label="Description"
                  hint="Internal — shown to staff, not to merchants."
                >
                  <textarea
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 outline-none sm:col-span-2"
                    style={{ ...inputStyle(C), resize: 'vertical' }}
                  />
                </Field>
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(e) =>
                      update('status', e.target.value as AdminPlanStatus)
                    }
                    className="w-full px-3 py-2 outline-none"
                    style={inputStyle(C)}
                  >
                    {PLAN_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <ToggleRow
                  checked={form.is_custom}
                  onChange={(v) => update('is_custom', v)}
                  label="Custom (one-off) plan"
                  hint="Marks a per-account negotiated plan distinct from the standard catalog."
                />
                <ToggleRow
                  checked={form.trial_enabled}
                  onChange={(v) => update('trial_enabled', v)}
                  label="Trial enabled"
                  hint="Trial length in days configures the trialing window on assignment."
                />
              </div>

              {form.trial_enabled ? (
                <div className="mt-4 sm:max-w-xs">
                  <Field label="Trial days">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.trial_days}
                      onChange={(e) => update('trial_days', e.target.value)}
                      className="w-full px-3 py-2 outline-none"
                      style={{ ...inputStyle(C), fontFamily: monoFont }}
                      placeholder="14"
                    />
                  </Field>
                </div>
              ) : null}
            </Card>

            <Card style={{ padding: 20 }}>
              <SectionKicker>Catalog</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Scope"
                  hint={
                    isCreate
                      ? 'Per-site plans sit on one site (Free, Protect, Business). Account plans sit on the whole account (Standard, agency).'
                      : 'Scope is set at create and cannot change.'
                  }
                  required
                >
                  {isCreate ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <ScopeChoice
                        value="site"
                        current={form.scope}
                        label="Per site"
                        onChoose={() => update('scope', 'site')}
                      />
                      <ScopeChoice
                        value="account"
                        current={form.scope}
                        label="Whole account"
                        onChoose={() => update('scope', 'account')}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.body,
                        padding: '8px 12px',
                        border: `1px solid ${C.borderSoft}`,
                        borderRadius: 2,
                        background: C.surface2,
                      }}
                    >
                      {form.scope === 'site' ? 'Per site' : 'Whole account'}
                    </div>
                  )}
                </Field>
                <Field
                  label="Catalog version"
                  hint="2 = Plans v2 (per-site plans, features, add-ons). 1 = legacy."
                >
                  {!isCreate ? (
                    <div
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.body,
                        padding: '8px 12px',
                        background: C.surface2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                      }}
                    >
                      {form.catalog_version === 2 ? '2 (Plans v2)' : '1 (legacy)'}
                    </div>
                  ) : (
                  <select
                    value={String(form.catalog_version)}
                    onChange={(e) =>
                      update(
                        'catalog_version',
                        (Number(e.target.value) as AdminPlanCatalogVersion),
                      )
                    }
                    className="w-full px-3 py-2 outline-none"
                    style={inputStyle(C)}
                  >
                    <option value="2">2 (Plans v2)</option>
                    <option value="1">1 (legacy)</option>
                  </select>
                  )}
                </Field>
                {form.scope === 'account' ? (
                  <Field
                    label="Included sites get"
                    hint="For agency plans — the site plan an included client site uses. Only non-archived site plans appear."
                  >
                    <select
                      value={form.included_site_plan_id ?? ''}
                      onChange={(e) =>
                        update(
                          'included_site_plan_id',
                          e.target.value === '' ? null : e.target.value,
                        )
                      }
                      className="w-full px-3 py-2 outline-none"
                      style={inputStyle(C)}
                    >
                      <option value="">(none)</option>
                      {sitePlans
                        .filter(
                          (p) =>
                            p.plan.scope === 'site' &&
                            p.plan.status !== 'archived',
                        )
                        .map((p) => (
                          <option key={p.plan.plan_id} value={p.plan.plan_id}>
                            {p.plan.name} · {p.plan.status}
                          </option>
                        ))}
                    </select>
                  </Field>
                ) : null}
              </div>
              <div className="mt-4">
                <ToggleRow
                  checked={form.is_default}
                  onChange={(v) => update('is_default', v)}
                  disabled={form.status !== 'active'}
                  label={`Default for new ${form.scope === 'site' ? 'sites' : 'accounts'}`}
                  hint={
                    form.status === 'active'
                      ? 'At most one active default per scope — flipping this on moves the default here.'
                      : 'Only active plans can be default. Set status to active first.'
                  }
                />
              </div>
            </Card>

            <Card style={{ padding: 20 }}>
              <SectionKicker>Pricing</SectionKicker>
              <div className="grid sm:grid-cols-2 gap-4">
                <PriceFieldset
                  label="Monthly"
                  amount={form.monthly_amount}
                  currency={form.monthly_currency}
                  onAmount={(v) => update('monthly_amount', v)}
                  onCurrency={(v) => update('monthly_currency', v)}
                />
                <YearlyPriceFieldset
                  label="Yearly"
                  amount={form.yearly_amount}
                  currency={form.yearly_currency}
                  monthlyAmount={form.monthly_amount}
                  onAmount={(v) => update('yearly_amount', v)}
                  onCurrency={(v) => update('yearly_currency', v)}
                />
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.muted,
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                Amounts are in the currency&rsquo;s minor unit (USD cents, JPY
                yen). Leave an interval blank to omit it. Saving REPLACES the
                price set for this plan.
              </div>
            </Card>

            {form.catalog_version === 2 ? (
              <Card style={{ padding: 20 }}>
                <SectionKicker>Features</SectionKicker>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    marginBottom: 14,
                    lineHeight: 1.5,
                  }}
                >
                  Features the plan grants.{' '}
                  {form.scope === 'site'
                    ? 'Site features only — account features sit on account plans.'
                    : 'Account features only — site features sit on site plans.'}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {featuresForScope(form.scope).map((f) => (
                    <FeatureRow
                      key={f}
                      feature={f}
                      checked={form.features.has(f)}
                      onChange={(v) => {
                        setForm((cur) => {
                          const next = new Set(cur.features);
                          if (v) next.add(f);
                          else next.delete(f);
                          return { ...cur, features: next };
                        });
                      }}
                    />
                  ))}
                </div>
              </Card>
            ) : null}

            <Card style={{ padding: 20 }}>
              <SectionKicker>Entitlements</SectionKicker>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  marginBottom: 14,
                  lineHeight: 1.5,
                }}
              >
                Each enabled dimension defines a quota. Limit blank =
                unlimited. Page loads, orders and assistant requests are
                always <strong>soft</strong> (ADR-0263) — going over
                records and emails, it never blocks.
              </div>
              {form.catalog_version === 2 ? (
                <div className="mb-4 sm:max-w-sm">
                  <Field
                    label="Overage allowance"
                    hint="Percent past each finite limit before the next-tier banner. 20 = usage up to 120% of the limit still counts as within the plan's overage pad (ADR-0263 §7.1)."
                  >
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.overage_pct}
                        onChange={(e) => update('overage_pct', e.target.value)}
                        className="w-full px-3 py-2 outline-none"
                        style={{
                          ...inputStyle(C),
                          fontFamily: monoFont,
                          paddingRight: 28,
                        }}
                        placeholder="0"
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontFamily: monoFont,
                          fontSize: 12,
                          color: C.muted,
                        }}
                      >
                        %
                      </span>
                    </div>
                  </Field>
                </div>
              ) : null}
              <div className="grid grid-cols-1 gap-3">
                {(form.catalog_version === 2
                  ? dimensionsForScope(form.scope)
                  : ALL_DIMENSIONS
                ).map((d) => (
                  <EntitlementRowEditor
                    key={d}
                    dimension={d}
                    row={form.entitlements[d]}
                    onChange={(patch) => updateEntitlement(d, patch)}
                  />
                ))}
              </div>
            </Card>

            <div
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1"
            >
              <div className="flex items-center gap-3 flex-wrap">
                {saveError !== null ? (
                  <span
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.coralFg,
                    }}
                  >
                    {saveError}
                  </span>
                ) : successAt !== null && Date.now() - successAt < 4000 ? (
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.limeFg,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Saved
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {!isCreate && form.status !== 'archived' ? (
                  <button
                    type="button"
                    onClick={() => setArchiveOpen(true)}
                    disabled={saving || archiving}
                    className="flex items-center gap-2 justify-center"
                    style={{
                      background: 'transparent',
                      color: C.muted,
                      fontFamily: sansFont,
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '10px 14px',
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      minHeight: 44,
                    }}
                  >
                    <Archive size={13} aria-hidden="true" />
                    Archive plan
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    background: C.lime,
                    color: C.ink,
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '10px 16px',
                    borderRadius: 2,
                    border: 0,
                    opacity: saving ? 0.6 : 1,
                    minHeight: 44,
                  }}
                >
                  {saving ? 'Saving…' : isCreate ? 'Create plan' : 'Save plan'}
                </button>
              </div>
            </div>
          </form>

          <ConfirmDialog
            open={archiveOpen}
            title={`Archive ${form.name || 'this plan'}?`}
            body="Archived plans can&rsquo;t be assigned to new accounts. Existing subscriptions on the plan stay in place. You can un-archive later by switching status back to active."
            confirmLabel="Archive"
            tone="coral"
            pending={archiving}
            onConfirm={() => void doArchive()}
            onCancel={() => {
              if (archiving) return;
              setArchiveOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}

function PlanHeader({
  isCreate,
  name,
  status,
  scope,
  isDefault,
  catalogVersion,
  planId,
  updatedAt,
}: {
  isCreate: boolean;
  name: string;
  status: AdminPlanStatus;
  scope: AdminPlanScope;
  isDefault: boolean;
  catalogVersion: AdminPlanCatalogVersion;
  planId: string | null;
  updatedAt: string | null;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div className="min-w-0">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 10,
            wordBreak: 'break-all',
          }}
        >
          {isCreate ? 'New plan' : `Plan · ${planId ?? ''}`}
        </div>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(28px, 6vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
            margin: 0,
            wordBreak: 'break-word',
          }}
        >
          {name.trim().length === 0 ? 'Untitled plan' : name}
        </h1>
        {updatedAt !== null ? (
          <div
            style={{
              marginTop: 10,
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
            }}
          >
            Updated {formatIsoCompact(updatedAt)}
          </div>
        ) : null}
      </div>
      {!isCreate ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={planStatusTone(status)}>{status}</Pill>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
            }}
          >
            {scope === 'site' ? 'Per site' : 'Account'} · v{catalogVersion}
          </span>
          {isDefault ? <Pill tone="lime">Default</Pill> : null}
        </div>
      ) : null}
    </div>
  );
}

function SectionKicker({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function PriceFieldset({
  label,
  amount,
  currency,
  onAmount,
  onCurrency,
}: {
  label: string;
  amount: string;
  currency: string;
  onAmount: (v: string) => void;
  onCurrency: (v: string) => void;
}): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          className="flex-1 px-3 py-2 outline-none"
          style={{ ...inputStyle(C), fontFamily: monoFont }}
          placeholder="0"
          aria-label={`${label} amount in minor units`}
        />
        <input
          type="text"
          value={currency}
          onChange={(e) => onCurrency(e.target.value.toLowerCase())}
          maxLength={3}
          className="px-3 py-2 outline-none uppercase"
          style={{
            ...inputStyle(C),
            fontFamily: monoFont,
            width: 88,
            textAlign: 'center',
          }}
          placeholder="USD"
          aria-label={`${label} currency`}
        />
      </div>
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
  disabled?: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <label
      className="flex items-start gap-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: '12px 14px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: sansFont,
        fontSize: 13,
        color: C.text,
        lineHeight: 1.5,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{
          marginTop: 2,
          accentColor: C.limeFg,
          flexShrink: 0,
        }}
      />
      <span>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span
          style={{
            display: 'block',
            fontSize: 11,
            color: C.muted,
            marginTop: 2,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </span>
      </span>
    </label>
  );
}

function EntitlementRowEditor({
  dimension,
  row,
  onChange,
}: {
  dimension: AdminPlanDimension;
  row: EntitlementRow;
  onChange: (patch: Partial<EntitlementRow>) => void;
}): JSX.Element {
  const C = useC();
  const label = dimensionLabel(dimension);
  const hint = dimensionHint(dimension);
  const softLocked = SOFT_ONLY_DIMENSIONS_MIRROR.has(dimension);
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 14,
      }}
    >
      <label
        className="flex items-center gap-3"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        <input
          type="checkbox"
          checked={row.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          style={{ accentColor: C.limeFg, flexShrink: 0 }}
        />
        {label}
      </label>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          marginTop: 4,
          marginLeft: 26,
          lineHeight: 1.5,
        }}
      >
        {hint}
      </div>
      {row.enabled ? (
        <div className="grid sm:grid-cols-2 gap-3 mt-3" style={{ marginLeft: 26 }}>
          <div>
            <label
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Limit
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={row.limit}
              onChange={(e) => onChange({ limit: e.target.value })}
              className="w-full px-3 py-2 outline-none"
              style={{ ...inputStyle(C), fontFamily: monoFont }}
              placeholder="Unlimited"
              aria-label={`${label} limit`}
            />
          </div>
          <div>
            <label
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Behavior{' '}
              <span style={{ color: C.dim, textTransform: 'none', letterSpacing: 1 }}>
                (applies once enforcement ships)
              </span>
            </label>
            {softLocked ? (
              <div
                style={{
                  ...inputStyle(C),
                  padding: '8px 12px',
                  fontFamily: monoFont,
                  color: C.body,
                }}
              >
                Soft · never blocks
              </div>
            ) : (
              <select
                value={row.behavior}
                onChange={(e) =>
                  onChange({
                    behavior: e.target.value as AdminPlanEntitlementBehavior,
                  })
                }
                className="w-full px-3 py-2 outline-none"
                style={inputStyle(C)}
                aria-label={`${label} behavior`}
              >
                {BEHAVIOR_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ScopeChoice({
  value,
  current,
  label,
  onChoose,
}: {
  value: AdminPlanScope;
  current: AdminPlanScope;
  label: string;
  onChoose: () => void;
}): JSX.Element {
  const C = useC();
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={active}
      style={{
        fontFamily: sansFont,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        padding: '10px 14px',
        border: `1px solid ${active ? C.limeFg : C.border}`,
        background: active ? C.limeFg : 'transparent',
        color: active ? C.ink : C.text,
        borderRadius: 2,
        minHeight: 44,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function FeatureRow({
  feature,
  checked,
  onChange,
}: {
  feature: AdminPlanFeature;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  const C = useC();
  return (
    <label
      className="flex items-start gap-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: '12px 14px',
        cursor: 'pointer',
        fontFamily: sansFont,
        fontSize: 13,
        color: C.text,
        lineHeight: 1.5,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          marginTop: 2,
          accentColor: C.limeFg,
          flexShrink: 0,
        }}
      />
      <span>
        <span style={{ fontWeight: 500 }}>{featureLabel(feature)}</span>
        <span
          style={{
            display: 'block',
            fontSize: 11,
            color: C.muted,
            marginTop: 2,
            lineHeight: 1.5,
          }}
        >
          {featureHint(feature)}
        </span>
      </span>
    </label>
  );
}

function YearlyPriceFieldset(props: {
  label: string;
  amount: string;
  currency: string;
  monthlyAmount: string;
  onAmount: (v: string) => void;
  onCurrency: (v: string) => void;
}): JSX.Element {
  const C = useC();
  const canFill = tenXMonthly(props.monthlyAmount).length > 0;
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 12,
      }}
    >
      <div
        className="flex items-center justify-between gap-2"
        style={{ marginBottom: 8 }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          {props.label}
        </div>
        <button
          type="button"
          onClick={() => props.onAmount(tenXMonthly(props.monthlyAmount))}
          disabled={!canFill}
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: canFill ? C.text : C.dim,
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '4px 8px',
            cursor: canFill ? 'pointer' : 'not-allowed',
          }}
          title="Set yearly to monthly × 10 (two months free)"
        >
          10 × monthly (two months free)
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={props.amount}
          onChange={(e) => props.onAmount(e.target.value)}
          className="flex-1 px-3 py-2 outline-none"
          style={{ ...inputStyle(C), fontFamily: monoFont }}
          placeholder="0"
          aria-label={`${props.label} amount in minor units`}
        />
        <input
          type="text"
          value={props.currency}
          onChange={(e) => props.onCurrency(e.target.value.toLowerCase())}
          maxLength={3}
          className="px-3 py-2 outline-none uppercase"
          style={{
            ...inputStyle(C),
            fontFamily: monoFont,
            width: 88,
            textAlign: 'center',
          }}
          placeholder="USD"
          aria-label={`${props.label} currency`}
        />
      </div>
    </div>
  );
}

function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
  };
}
